import { useLeanProductQuery } from "@faire/deprecated-retailer-visitor-shared/views/product/utils/useLeanProductQuery";
import { BrandTokenOrBrandAlias } from "@faire/faire-direct/lib/visitor/faireDirectTypes";
import { useQuery as useProductBasedCollectionTokenQuery } from "@faire/web-api--source/endpoints/www/api/product-based-collections/productBasedCollectionToken/retailer/get-hooks";
import { match as matchBrandPageRoute } from "@faire/web-api--source/routes/www/brand/brandToken/match";
import { match as matchFaireDirectLandingPageRoute } from "@faire/web-api--source/routes/www/direct/brandAlias/match";
import { match as matchFaireDirectSalesRepRoute } from "@faire/web-api--source/routes/www/direct/brandAlias/rep/salesRepCode/match";
import { match as matchProductBasedCollectionRoute } from "@faire/web-api--source/routes/www/product-based-collection/product_based_collection_token/match";
import { match as matchProductRoute } from "@faire/web-api--source/routes/www/product/productToken/match";
import { match as matchWelcomeRoute } from "@faire/web-api--source/routes/www/welcome/r/match";
import { FAIRE_DIRECT_BRAND_QUERY_PARAM } from "@faire/web/common/routes/faire-direct/FAIRE_DIRECT_BRAND_QUERY_PARAM";
import { removeLocaleFromUrl } from "@faire/web/common/routing/util";
import {
  ReadonlyURLSearchParams,
  usePathname,
  useSearchParams,
} from "next/navigation";

export const useFindFaireDirectBrandTokenOrAliasForCurrentPage =
  (): BrandTokenOrBrandAlias => {
    const pathname = usePathname();
    const pathWithoutQueryParamsAndLocale = pathname
      ? removeLocaleFromUrl(pathname)
      : "";
    const searchParams = useSearchParams() || new URLSearchParams();

    const brandTokenFromProductBasedCollection =
      useMaybeGetBrandTokenFromProductBasedCollectionPath(
        pathWithoutQueryParamsAndLocale
      );

    const brandTokenFromProductPage = useMaybeGetBrandTokenFromProductPagePath(
      pathWithoutQueryParamsAndLocale
    );

    const brandAliasFromFaireDirectLandingPageRoutes =
      getBrandAliasFromFaireDirectLandingPageRoutes(
        pathWithoutQueryParamsAndLocale
      );
    const brandAliasFromFaireDirectWidgetSignupFlow =
      getBrandAliasFromFaireDirectWidgetSignupFlow(
        pathWithoutQueryParamsAndLocale,
        searchParams
      );
    const brandAlias =
      brandAliasFromFaireDirectLandingPageRoutes ||
      brandAliasFromFaireDirectWidgetSignupFlow;
    if (brandAlias) {
      return {
        brandAlias,
      };
    }

    const brandTokenFromBrandPage = maybeGetBrandTokenFromBrandPage(
      pathWithoutQueryParamsAndLocale
    );

    const brandToken =
      brandTokenFromBrandPage ||
      brandTokenFromProductBasedCollection ||
      brandTokenFromProductPage;

    return { brandToken };
  };

const getBrandAliasFromFaireDirectLandingPageRoutes = (path: string) => {
  const isFaireDirectLandingPage = matchFaireDirectLandingPageRoute(path);
  if (isFaireDirectLandingPage) {
    return isFaireDirectLandingPage.params.brandAlias;
  }
  const isFaireDirectSalesRepPage = matchFaireDirectSalesRepRoute(path);
  if (isFaireDirectSalesRepPage) {
    return isFaireDirectSalesRepPage.params.salesRepCode;
  }
  return undefined;
};

const getBrandAliasFromFaireDirectWidgetSignupFlow = (
  pathWithoutQueryParamsAndLocale: string,
  searchParams: ReadonlyURLSearchParams
) => {
  const isSignupFlow = matchWelcomeRoute(pathWithoutQueryParamsAndLocale);
  if (!isSignupFlow) {
    return undefined;
  }
  return searchParams.get(FAIRE_DIRECT_BRAND_QUERY_PARAM) || undefined;
};

const useMaybeGetBrandTokenFromProductBasedCollectionPath = (path: string) => {
  const isProductBasedCollectionPage = matchProductBasedCollectionRoute(path);
  const productBasedCollectionToken =
    isProductBasedCollectionPage?.params.productBasedCollectionToken;

  const { data: productBasedCollectionData } =
    useProductBasedCollectionTokenQuery(
      productBasedCollectionToken ?? "",
      undefined,
      {
        enabled:
          !!productBasedCollectionToken && !!isProductBasedCollectionPage,
      }
    );

  return productBasedCollectionData?.product_collection?.brand_token;
};

const useMaybeGetBrandTokenFromProductPagePath = (path: string) => {
  const isProductPage = matchProductRoute(path);
  const productToken = isProductPage?.params.productToken;
  const { data: productData } = useLeanProductQuery(productToken, {
    enabled: !!productToken && !!isProductPage,
  });
  return productData?.brand?.token;
};

const maybeGetBrandTokenFromBrandPage = (path: string) => {
  const isBrandPage = matchBrandPageRoute(path);
  return isBrandPage?.params.brandToken;
};
